import React, { useState, useEffect } from 'react';
import { Mic, Loader2, FileText, Calendar, TrendingUp, Cloud, Settings, Home, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReportSettings from './ReportSettings';

const DailyReportGenerator: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dateFromUrl = searchParams.get('date');

  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedReport, setGeneratedReport] = useState<any>(null);
  const [showSettings, setShowSettings] = useState(false);
  // JST基準で今日の日付を取得
  const getJSTToday = () => {
    const now = new Date();
    const jstNow = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return jstNow.toISOString().split('T')[0];
  };
  const [reportDate, setReportDate] = useState(dateFromUrl || getJSTToday());

  // 前日に移動
  const handlePreviousDay = () => {
    const currentDate = new Date(reportDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setReportDate(currentDate.toISOString().split('T')[0]);
    setGeneratedReport(null); // レポートをリセット
  };

  // 翌日に移動
  const handleNextDay = () => {
    const currentDate = new Date(reportDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setReportDate(currentDate.toISOString().split('T')[0]);
    setGeneratedReport(null); // レポートをリセット
  };

  const handleGenerate = async () => {
    if (!comment.trim()) {
      alert('感想を入力してください');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/daily-reports/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_comment: comment,
          report_date: reportDate
        })
      });

      const data = await res.json();

      if (data.success) {
        setGeneratedReport(data.data);
      } else {
        alert('日報生成に失敗しました: ' + data.message);
      }
    } catch (error) {
      console.error('日報生成エラー:', error);
      alert('エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!generatedReport) return;

    const reportDateFormatted = new Date(reportDate).toLocaleDateString('ja-JP');
    const reportHTML = `
<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>和とん日報 - ${reportDateFormatted}</title>
  <style>
    @page {
      size: A4;
      margin: 15mm 20mm;
    }
    body {
      font-family: 'Hiragino Sans', 'Yu Gothic', sans-serif;
      background: white;
      margin: 0;
      padding: 20px;
      color: black;
    }
    .header {
      border-bottom: 4px solid #f97316;
      padding-bottom: 16px;
      margin-bottom: 24px;
    }
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .title {
      font-size: 24px;
      font-weight: bold;
      color: #f97316;
    }
    .date-info {
      text-align: right;
      font-size: 14px;
      color: #666;
    }
    .content {
      white-space: pre-wrap;
      line-height: 1.8;
      font-size: 14px;
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-content">
      <h1 class="title">🍖 和とん日報</h1>
      <div class="date-info">
        <div>📅 ${reportDateFormatted}</div>
        <div>🌤️ ${generatedReport.weather?.description || ''} / ${generatedReport.weather?.temp?.toFixed(1) || ''}℃</div>
      </div>
    </div>
  </div>
  <div class="content">
${generatedReport.report || ''}
  </div>
</body>
</html>
`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(reportHTML);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 100);
    } else {
      alert('ポップアップがブロックされています。ブラウザの設定を確認してください。');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileText className="text-orange-500" />
          🔥 AI日報生成
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors"
          >
            <Home size={20} />
            HOME
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Settings size={20} />
            設定
          </button>
        </div>
      </div>

      {showSettings && <ReportSettings onClose={() => setShowSettings(false)} />}

      {/* 入力エリア */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 print:hidden">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            📅 日報の日付
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousDay}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center"
              title="前日"
            >
              <ChevronLeft size={20} />
            </button>
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              max={getJSTToday()}
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            <button
              onClick={handleNextDay}
              className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center"
              title="翌日"
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            ← → ボタンで前日・翌日に移動できます
          </p>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-2">
          💭 その日の振り返り・感想
        </label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="その日の感想を入力してください

📱 スマホの場合
キーボードの🎤マークを押して音声入力

💻 PCの場合
そのままキーボードで入力

例:
「今日はお客さん多かったなぁ。ロースかつが特に人気で、午後には売り切れそうだった。明日は雨らしいから、テイクアウトの仕込みを増やしたい。」"
          className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
        />

        <div className="mt-4 flex gap-3">
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg font-semibold
                     hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2 transition-all transform hover:scale-105"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                AIが日報を生成中...
              </>
            ) : (
              <>
                <FileText size={20} />
                🔥 日報を生成する
              </>
            )}
          </button>
        </div>
      </div>

      {/* 生成された日報 */}
      {generatedReport && (
        <div className="bg-white rounded-lg shadow-lg p-8 print:shadow-none print:border-0 print:rounded-none" id="report-content">
          {/* ヘッダー */}
          <div className="border-b-4 border-orange-500 pb-4 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-orange-600 flex items-center gap-2">
                🍖 和とん日報
              </h2>
              <div className="text-right text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Calendar size={16} />
                  {new Date(reportDate).toLocaleDateString('ja-JP')}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Cloud size={16} />
                  {generatedReport.weather?.description || ''} / {generatedReport.weather?.temp?.toFixed(1) || ''}℃
                </div>
              </div>
            </div>
          </div>

          {/* Markdown表示（簡易版） */}
          <div className="prose max-w-none whitespace-pre-wrap">
            {generatedReport.report}
          </div>

          {/* アクションボタン */}
          <div className="mt-8 flex gap-3 print:hidden">
            <button
              onClick={handlePrint}
              className="flex-1 bg-blue-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-600 flex items-center justify-center gap-2"
            >
              🖨️ 印刷
            </button>
            <button
              onClick={() => navigate('/reports/calendar')}
              className="flex-1 bg-green-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-600 flex items-center justify-center gap-2"
            >
              📅 カレンダーで確認
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReportGenerator;
